"use client";

/**
 * The home hero's live instrument: the WebGL Twin, its dial and the arrival layer. The
 * board pairs it with legends and answers in concept/groups/hero-instrument.tsx.
 */

import dynamic from "next/dynamic";
import { type ReactNode, useEffect, useRef, useState } from "react";
import twinStyles from "@/components/editorial/glass-twin/glass-twin.module.css";

import { DATA_LABEL_CLS } from "@/components/editorial";

const GlassTwin3D = dynamic(
  () =>
    import("@/components/editorial/glass-twin/glass-twin-3d").then(
      (module) => module.GlassTwin3D,
    ),
  { ssr: false },
);

const GlassTwinIngress = dynamic(
  () =>
    import("@/components/editorial/glass-twin/glass-twin-3d").then(
      (module) => module.GlassTwinIngress,
    ),
  { ssr: false },
);

export const INK_58 = "text-[color-mix(in_srgb,var(--theme-ink)_58%,transparent)]";

/* ================================================================= */
/*  The instrument, reusable                                          */
/* ================================================================= */

/** Mounts children once the wrapper scrolls near the viewport. */
function MountInView({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || mounted) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setMounted(true);
      },
      { rootMargin: "280px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mounted]);
  return (
    <div ref={ref} className={className}>
      {mounted ? children : null}
    </div>
  );
}

/** The home hero's dial, as it ships: two circles, 96 ticks, four bearings. */
const DIAL_TICKS = Array.from({ length: 96 }, (_, index) => ({
  angle: index * 3.75,
  major: index % 12 === 0,
}));

function Dial() {
  return (
    <svg
      className={twinStyles.dial}
      viewBox="0 0 560 560"
      role="presentation"
      aria-hidden="true"
    >
      <circle cx="280" cy="280" r="258" />
      <circle cx="280" cy="280" r="232" />
      {DIAL_TICKS.map(({ angle, major }) => (
        <line
          key={angle}
          x1="280"
          y1={major ? 22 : 29}
          x2="280"
          y2={major ? 48 : 40}
          transform={`rotate(${angle} 280 280)`}
        />
      ))}
      <text x="280" y="14" textAnchor="middle">0</text>
      <text x="548" y="284" textAnchor="start">90</text>
      <text x="280" y="557" textAnchor="middle">180</text>
      <text x="12" y="284" textAnchor="end">270</text>
    </svg>
  );
}

/**
 * The live instrument: the home's dial with the real WebGL twin in its hole.
 * The .gl3d-hole renders statically — it is the glass rim before the twin
 * mounts, and the section's arrival layer needs it in the DOM to aim at —
 * so only the WebGL itself waits for the viewport. `bare` is the twin
 * alone, filling its box, without the dial and the glass rim: the same
 * particles for a place that shows the Twin and not the instrument (the
 * home's story).
 */
export function LiveInstrument({
  scale = 2.25,
  sizeClass = "w-[min(70vw,26rem)]",
  label,
  onReady,
  bare = false,
}: {
  scale?: number;
  sizeClass?: string;
  label?: string;
  /** Called once the twin can draw, for a stage that starts its own clock then. */
  onReady?: () => void;
  bare?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className={`${twinStyles.particleStage} relative mx-auto aspect-square ${sizeClass}`}>
        {bare ? null : <Dial />}
        {/* rounded-full: the glass stays a circle on a page that squares its corners (one-system). */}
        <div className="gl3d-hole rounded-full" style={bare ? { inset: 0, border: "none" } : undefined}>
          <MountInView className="size-full">
            <GlassTwin3D particleScale={scale} onReady={onReady} />
          </MountInView>
        </div>
      </div>
      {label ? (
        <p className={`mt-4 text-center ${INK_58} ${DATA_LABEL_CLS}`}>{label}</p>
      ) : null}
    </div>
  );
}

/**
 * The hero's stage-wide arrival: the same 2D particulate the landing hero
 * runs behind its headline, converging on this section's own glass. The
 * component finds the nearest [data-twin-stage] ancestor and absorbs into
 * the first .gl3d-hole inside it, so each concept section carries the
 * attribute and mounts exactly one instrument. Sits under the content
 * (z-1 vs z-2), exactly as the home layers it.
 */
export function IngressLayer() {
  return (
    <div
      className={`${twinStyles.particleStage} pointer-events-none absolute inset-0 z-[1] max-lg:hidden`}
      aria-hidden="true"
    >
      <MountInView className="h-full w-full">
        <GlassTwinIngress particleScale={1.9} />
      </MountInView>
    </div>
  );
}
